import { Trash2 } from "lucide-react";

const STATUS_OPTIONS = [
  "new", "contacted", "documents_pending", "qualified", "confirmed", "not_proceeding",
];

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString() : "";
}

function DeleteButton({ id, onDelete, testId }) {
  return (
    <td>
      <button
        onClick={() => onDelete(id)}
        data-testid={testId}
        className="text-red-500 hover:text-red-700"
        aria-label="Delete"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </td>
  );
}

function ApplicationsHeader() {
  return (
    <tr className="text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
      <th className="p-3">Name</th><th>Email</th><th>Country</th><th>Institution</th><th>Destination</th><th>Interest</th><th>Docs</th><th>Status</th><th>Date</th><th></th>
    </tr>
  );
}

function DocsCell({ documents }) {
  const list = documents || [];
  if (list.length === 0) return <td><span className="text-slate-400 text-xs">—</span></td>;
  return (
    <td>
      <div className="flex flex-wrap gap-1 max-w-[220px]">
        {list.map((d, idx) => (
          <a
            key={d.id || idx}
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/files/${d.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`admin-doc-link-${d.id}`}
            title={d.filename || `Document ${idx + 1}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 hover:bg-red-100 text-[#D52B1E] text-[11px] font-semibold max-w-[200px]"
          >
            <span className="truncate">{d.filename || `File ${idx + 1}`}</span>
          </a>
        ))}
      </div>
    </td>
  );
}

function ApplicationRow({ item, onStatusChange, onDelete }) {
  const displayStatus = (item.status || "new").replace(/_/g, " ");
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-row-${item.id}`}>
      <td className="p-3 font-semibold text-slate-900">{item.full_name}</td>
      <td className="text-slate-600">{item.email}</td>
      <td className="text-slate-600">{item.country_of_residence || "—"}</td>
      <td className="text-slate-600">{item.institution || "—"}</td>
      <td className="text-slate-600">{item.preferred_destination || "—"}</td>
      <td className="text-slate-600">{item.area_of_interest || item.preferred_program || "—"}</td>
      <DocsCell documents={item.documents} />
      <td>
        <select
          value={STATUS_OPTIONS.includes(item.status) ? item.status : "new"}
          onChange={(e) => onStatusChange(item.id, e.target.value)}
          data-testid={`admin-status-${item.id}`}
          className="px-2 py-1 rounded-md bg-slate-100 text-xs font-semibold capitalize"
          title={displayStatus}
        >
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </td>
      <td className="text-slate-500 text-xs">{fmtDate(item.created_at)}</td>
      <DeleteButton id={item.id} onDelete={onDelete} testId={`admin-delete-${item.id}`} />
    </tr>
  );
}

function PartnersHeader() {
  return (
    <tr className="text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
      <th className="p-3">Organization</th><th>Contact</th><th>Email</th><th>Country</th><th>Students</th><th>Date</th><th></th>
    </tr>
  );
}

function PartnerRow({ item, onDelete }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-row-${item.id}`}>
      <td className="p-3 font-semibold text-slate-900">{item.organization_name}</td>
      <td>{item.contact_person}</td>
      <td className="text-slate-600">{item.email}</td>
      <td className="text-slate-600">{item.country || "—"}</td>
      <td className="text-slate-600">{item.number_of_students || "—"}</td>
      <td className="text-slate-500 text-xs">{fmtDate(item.created_at)}</td>
      <DeleteButton id={item.id} onDelete={onDelete} />
    </tr>
  );
}

function ContactsHeader() {
  return (
    <tr className="text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
      <th className="p-3">Name</th><th>Email</th><th>Country</th><th>Preferred</th><th>Date</th><th></th>
    </tr>
  );
}

function ContactRow({ item, onDelete }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-row-${item.id}`}>
      <td className="p-3 font-semibold text-slate-900">{item.full_name}</td>
      <td className="text-slate-600">{item.email}</td>
      <td className="text-slate-600">{item.country || "—"}</td>
      <td className="text-slate-600">{item.preferred_destination || "—"} / {item.preferred_program || "—"}</td>
      <td className="text-slate-500 text-xs">{fmtDate(item.created_at)}</td>
      <DeleteButton id={item.id} onDelete={onDelete} />
    </tr>
  );
}

function ProgramsHeader() {
  return (
    <tr className="text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
      <th className="p-3">Title</th><th>Category</th><th>Order</th><th></th>
    </tr>
  );
}

function ProgramRow({ item, onDelete }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-row-${item.id}`}>
      <td className="p-3 font-semibold text-slate-900">{item.title}</td>
      <td className="text-slate-600">{item.category}</td>
      <td className="text-slate-600">{item.order}</td>
      <DeleteButton id={item.id} onDelete={onDelete} />
    </tr>
  );
}

function PackagesHeader() {
  return (
    <tr className="text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
      <th className="p-3">Package</th><th>Destination</th><th>Duration</th><th>Price</th><th></th>
    </tr>
  );
}

function PackageRow({ item, onDelete }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-row-${item.id}`}>
      <td className="p-3 font-semibold text-slate-900">{item.name}</td>
      <td className="text-slate-600">{item.destination_name}</td>
      <td className="text-slate-600">{item.duration}</td>
      <td className="text-slate-600">{item.currency} {item.starting_price}</td>
      <DeleteButton id={item.id} onDelete={onDelete} />
    </tr>
  );
}

function EmptyState({ label }) {
  return <div className="p-10 text-center text-slate-500">{label}</div>;
}

const TAB_CONFIG = {
  applications: { Header: ApplicationsHeader, Row: ApplicationRow },
  partners: { Header: PartnersHeader, Row: PartnerRow },
  contacts: { Header: ContactsHeader, Row: ContactRow },
  programs: { Header: ProgramsHeader, Row: ProgramRow },
  packages: { Header: PackagesHeader, Row: PackageRow },
};

export default function AdminDataTable({ tab, items, loading, onDelete, onStatusChange }) {
  if (loading) return <EmptyState label="Loading…" />;
  if (items.length === 0) return <EmptyState label="No records yet." />;

  const config = TAB_CONFIG[tab];
  if (!config) return <EmptyState label="Unknown tab." />;
  const { Header, Row } = config;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-table">
        <thead className="bg-slate-50">
          <Header />
        </thead>
        <tbody>
          {items.map((it) => (
            <Row
              key={it.id}
              item={it}
              onDelete={onDelete}
              onStatusChange={onStatusChange}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
